import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Icon from './Icons';
import ErrorScreen from './ErrorScreen';
import BottomSheet from './BottomSheet';
import { photoUrl } from './photoUtils';
import { fieldOptionList, useFieldOptions } from './fieldOptions';
import { useGrowingMethods, SubstratePicker, formatSubstrate } from './GrowingMethodPicker';
import { plantDisplayName } from './nameUtils';

const STATUS_LABELS = {
  active: 'Active',
  sprouting: 'Sprouting',
  rooting: 'Rooting',
  rooted: 'Rooted',
  failed: 'Failed',
};

const STATUS_ORDER = ['active', 'sprouting', 'rooting', 'rooted'];

function statusBadge(status) {
  return (
    <span className={`prop-status-badge${status && status !== 'active' ? ` prop-status-badge--${status}` : ''}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ItemCard({ item, onAction, selectionMode, selected, onSelect }) {
  const pressTimer = useRef(null);
  const longPressTriggered = useRef(false);

  function startPress() {
    longPressTriggered.current = false;
    pressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      onSelect?.(item.id, true);
    }, 500);
  }
  function cancelPress() { clearTimeout(pressTimer.current); }

  function handleClick() {
    if (longPressTriggered.current) { longPressTriggered.current = false; return; }
    if (selectionMode) onSelect(item.id);
    else onAction(item);
  }

  const daysSince = item.last_checked_at
    ? Math.floor((Date.now() - new Date(item.last_checked_at).getTime()) / 86400000)
    : null;
  const daysActive = item.started_date
    ? Math.floor((Date.now() - new Date(item.started_date).getTime()) / 86400000)
    : null;

  return (
    <div
      className={`prop-item-card${selected ? ' prop-item-card--selected' : ''}`}
      onClick={handleClick}
      onMouseDown={startPress} onMouseUp={cancelPress} onMouseLeave={cancelPress}
      onTouchStart={startPress} onTouchEnd={cancelPress} onTouchMove={cancelPress}
    >
      <div className="prop-item-card-row">
        {selectionMode && (
          <div className={`prop-item-select-circle${selected ? ' prop-item-select-circle--selected' : ''}`}>
            {selected && <Icon name="check" size={11} />}
          </div>
        )}
        <span className="prop-item-label">{item.label || `Item ${item.id}`}</span>
        {statusBadge(item.status)}
      </div>
      <div className="prop-item-meta">
        {daysActive !== null && <span className="prop-item-days-active">{daysActive}d active</span>}
        {item.prop_substrate && <><span>·</span><span>{formatSubstrate(item.prop_substrate)}</span></>}
        {item.prop_enclosure && <><span>·</span><span>{item.prop_enclosure}</span></>}
        {item.prop_light_source_name && <><span>·</span><span>{item.prop_light_source_name}</span></>}
        {item.location_name && <><span>·</span><span>{item.location_name}</span></>}
        {daysSince !== null && (
          <span className={`prop-item-last-checked${daysSince > 7 ? ' prop-item-last-checked--overdue' : ''}`}>
            {daysSince === 0 ? 'checked today' : `${daysSince}d ago`}
          </span>
        )}
        {item.last_checked_at === null && item.last_checked_at !== undefined && (
          <span className="prop-item-last-checked">never checked</span>
        )}
      </div>
      {item.notes && <p className="prop-item-notes">{item.notes}</p>}
    </div>
  );
}

// ── Status progress pipeline ─────────────────────────────────────────────────

const PROP_PIPELINE = [
  { key: 'active',    label: 'Active' },
  { key: 'sprouting', label: 'Sprouting', hint: 'First signs of life — stem or leaf bud visible' },
  { key: 'rooting',   label: 'Rooting',   hint: 'Roots emerging or confirmed present' },
  { key: 'rooted',    label: 'Rooted',    hint: 'Established root system, ready for potting' },
  { key: 'plant',     label: 'Plant',     hint: 'Graduated to its own pot' },
];

function PropStatusProgress({ currentStatus }) {
  const currentIdx = PROP_PIPELINE.findIndex(s => s.key === currentStatus);
  return (
    <div className="round-prop-pipeline" style={{ margin: '0.5rem 0 0.75rem' }}>
      {PROP_PIPELINE.map((stage, i) => {
        const isDone    = i < currentIdx;
        const isCurrent = i === currentIdx;
        const isNext    = i === currentIdx + 1;
        return (
          <div key={stage.key} className="round-prop-pipeline-step">
            <div className="round-prop-pipeline-node">
              <div
                title={stage.hint || ''}
                className={`round-prop-pipeline-dot${isCurrent ? ' round-prop-pipeline-dot--current' : isDone ? ' round-prop-pipeline-dot--filled' : ''}`}
              />
              <span className={`round-prop-pipeline-label${isCurrent ? ' round-prop-pipeline-label--current' : isNext ? ' round-prop-pipeline-label--next' : ''}`}>
                {stage.label}
              </span>
            </div>
            {i < PROP_PIPELINE.length - 1 && (
              <div className={`round-prop-pipeline-line${isDone ? ' round-prop-pipeline-line--done' : ''}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Propagation setup fields (substrate + enclosure + light + heat) ───────────

function PropSetupFields({ values, onChange, enclosureOptions, lightSources, showNodePosition = false }) {
  const methods = useGrowingMethods();
  const [methodFilter, setMethodFilter] = useState('');

  // Substrates filtered by selected method, or all substrates when none selected
  const substrates = methodFilter
    ? (methods.find(m => m.name === methodFilter)?.substrates ?? [])
    : (() => {
        const seen = new Set();
        const result = [];
        for (const m of methods) {
          for (const s of (m.substrates || [])) {
            if (!seen.has(s.name)) { seen.add(s.name); result.push({ id: s.id, name: s.name }); }
          }
        }
        return result.sort((a, b) => a.name.localeCompare(b.name));
      })();

  return (
    <>
      <div className="form-field">
        <label className="form-label">Growing method</label>
        <select
          className="form-input"
          value={methodFilter}
          onChange={e => { setMethodFilter(e.target.value); onChange('prop_substrate', ''); }}
        >
          <option value="">— All methods —</option>
          {methods.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
      </div>
      <SubstratePicker
        key={methodFilter}
        substrates={substrates}
        value={values.prop_substrate || ''}
        onChange={v => onChange('prop_substrate', v)}
        label="Substrate"
        methodName={methodFilter}
      />

      {/* ── Environment ─────────────────────────────────────────────── */}
      <div className="prop-setup-divider">
        <div className="prop-setup-divider-line" />
        <span className="prop-setup-divider-label">Environment</span>
        <div className="prop-setup-divider-line" />
      </div>

      <div className="form-field">
        <label className="form-label">Enclosure</label>
        <select value={values.prop_enclosure || ''} onChange={e => onChange('prop_enclosure', e.target.value)} className="form-input">
          <option value="">— None —</option>
          {enclosureOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="form-field">
        <label className="form-label">Light source</label>
        <select value={values.prop_light_source_id || ''} onChange={e => onChange('prop_light_source_id', e.target.value ? parseInt(e.target.value, 10) : null)} className="form-input">
          <option value="">— None —</option>
          {lightSources.map(ls => <option key={ls.id} value={ls.id}>{ls.name}</option>)}
        </select>
      </div>

      {/* Heat mat — iOS-style toggle with inline temperature target */}
      <div className="form-field">
        <label className="form-label">Heat mat</label>
        <div className="prop-toggle-row">
          <span style={{ flex: 1, fontSize: '0.88rem', color: Boolean(values.prop_heat_mat) ? 'var(--text)' : 'var(--text-light)' }}>
            {Boolean(values.prop_heat_mat) ? 'Enabled' : 'Off'}
          </span>
          {Boolean(values.prop_heat_mat) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <input
                type="number"
                value={values.prop_heat_target_f || ''}
                onChange={e => onChange('prop_heat_target_f', e.target.value ? parseInt(e.target.value, 10) : null)}
                className="prop-heat-input"
                placeholder="80"
                min="60" max="110"
              />
              <span className="prop-heat-unit-label">°F target</span>
            </div>
          )}
          <label className="prop-toggle-switch">
            <input
              type="checkbox"
              checked={Boolean(values.prop_heat_mat)}
              onChange={e => onChange('prop_heat_mat', e.target.checked ? 1 : 0)}
            />
            <span className="prop-toggle-track" />
          </label>
        </div>
      </div>

      {showNodePosition && (
        <div className="form-field">
          <label className="form-label">Node position (from tip)</label>
          <input
            type="number"
            value={values.source_node_position || ''}
            onChange={e => onChange('source_node_position', e.target.value ? parseInt(e.target.value, 10) : null)}
            className="form-input"
            placeholder="e.g. 1, 2, 3…"
            min="1"
          />
        </div>
      )}
    </>
  );
}

function ItemActionSheet({ item, lossReasonOptions, enclosureOptions, lightSources, onClose, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [failureReason, setFailureReason] = useState(item.failure_reason || '');
  const [graduateName, setGraduateName] = useState('');
  const [rootLength, setRootLength] = useState(item.root_length_mm ? String(item.root_length_mm) : '');
  const [mode, setMode] = useState(null); // 'fail' | 'graduate' | 'edit' | null
  const [editValues, setEditValues] = useState({
    label:               item.label || '',
    location_id:         item.location_id ? String(item.location_id) : '',
    prop_substrate:      item.prop_substrate || '',
    prop_enclosure:      item.prop_enclosure || '',
    prop_light_source_id: item.prop_light_source_id || '',
    prop_heat_mat:       item.prop_heat_mat || 0,
    prop_heat_target_f:  item.prop_heat_target_f || '',
    source_node_position: item.source_node_position || '',
    notes:               item.notes || '',
  });
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    fetch('/locations')
      .then(r => r.ok ? r.json() : [])
      .then(data => setLocations(data))
      .catch(() => {});
  }, []);

  async function patchItem(updates) {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/propagation-items/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onUpdated(updated);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleGraduate() {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/propagation-items/${item.id}/graduate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plant_name: graduateName.trim() || null,
          root_length_mm: rootLength ? parseInt(rootLength, 10) : null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      onUpdated(null); // null signals full reload
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleLooksGood() {
    await patchItem({ last_checked_at: new Date().toISOString().split('T')[0] });
  }

  async function handleSaveEdit() {
    const payload = {
      label:                editValues.label.trim() || null,
      location_id:          editValues.location_id ? parseInt(editValues.location_id, 10) : null,
      prop_substrate:       editValues.prop_substrate || null,
      prop_enclosure:       editValues.prop_enclosure || null,
      prop_light_source_id: editValues.prop_light_source_id ? parseInt(editValues.prop_light_source_id, 10) : null,
      prop_heat_mat:        editValues.prop_heat_mat ? 1 : 0,
      prop_heat_target_f:   editValues.prop_heat_mat && editValues.prop_heat_target_f ? parseInt(editValues.prop_heat_target_f, 10) : null,
      source_node_position: editValues.source_node_position ? parseInt(editValues.source_node_position, 10) : null,
      notes:                editValues.notes || null,
    };
    await patchItem(payload);
  }

  const nextStatus = STATUS_ORDER[STATUS_ORDER.indexOf(item.status) + 1];

  return (
    <div className="prop-action-sheet">
      {item.status !== 'failed' && <PropStatusProgress currentStatus={item.status} />}
      {error && <p className="health-save-error">{error}</p>}

      {mode === 'edit' ? (
        <div>
          <div className="form-grid" style={{ marginBottom: '0.5rem' }}>
            <div className="form-field">
              <label className="form-label">Label</label>
              <input
                type="text"
                className="form-input"
                value={editValues.label}
                onChange={e => setEditValues(prev => ({ ...prev, label: e.target.value }))}
                placeholder={`Item ${item.id}`}
              />
            </div>
            <div className="form-field">
              <label className="form-label">Location</label>
              <select
                className="form-input"
                value={editValues.location_id}
                onChange={e => setEditValues(prev => ({ ...prev, location_id: e.target.value }))}
              >
                <option value="">— None —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <PropSetupFields
            values={editValues}
            onChange={(k, v) => setEditValues(prev => ({ ...prev, [k]: v }))}
            enclosureOptions={enclosureOptions}
            lightSources={lightSources}
            showNodePosition={['cutting', 'wet_stick', 'air_layer'].includes(item.propagation_method)}
          />
          <div className="form-field">
            <label className="form-label">Notes</label>
            <textarea
              value={editValues.notes}
              onChange={e => setEditValues(prev => ({ ...prev, notes: e.target.value }))}
              className="form-input"
              rows={2}
              placeholder="Optional notes"
            />
          </div>
          <div className="prop-sheet-form-actions">
            <button className="btn btn-secondary" onClick={() => setMode(null)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : mode === 'fail' ? (
        <div>
          <label className="form-label" style={{ marginBottom: '0.35rem' }}>Failure reason</label>
          <select
            value={failureReason}
            onChange={e => setFailureReason(e.target.value)}
            className="form-input"
            style={{ marginBottom: '0.75rem' }}
          >
            <option value="">— Unknown —</option>
            {lossReasonOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="prop-sheet-form-actions">
            <button className="btn btn-secondary" onClick={() => setMode(null)} disabled={saving}>Cancel</button>
            <button
              className="btn btn-danger"
              onClick={() => patchItem({ status: 'failed', failure_reason: failureReason || null })}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Mark failed'}
            </button>
          </div>
        </div>
      ) : mode === 'graduate' ? (
        <div>
          <div className="form-field" style={{ marginBottom: '0.5rem' }}>
            <label className="form-label">Common name (optional)</label>
            <input
              type="text"
              value={graduateName}
              onChange={e => setGraduateName(e.target.value)}
              className="form-input"
              placeholder="Leave blank to use species name only"
            />
          </div>
          <div className="form-field" style={{ marginBottom: '0.75rem' }}>
            <label className="form-label">Root length at potting (mm, optional)</label>
            <input
              type="number"
              value={rootLength}
              onChange={e => setRootLength(e.target.value)}
              className="form-input"
              placeholder="e.g. 25"
            />
          </div>
          <div className="prop-sheet-form-actions">
            <button className="btn btn-secondary" onClick={() => setMode(null)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleGraduate} disabled={saving}>
              {saving ? 'Creating…' : 'Graduate to plant'}
            </button>
          </div>
        </div>
      ) : (
        <div className="prop-sheet-action-list">
          <button className="prop-sheet-action" onClick={handleLooksGood} disabled={saving}>
            Looks good — check in
          </button>
          {nextStatus && (
            <button
              className="prop-sheet-action"
              onClick={() => patchItem({ status: nextStatus })}
              disabled={saving}
            >
              Advance → {STATUS_LABELS[nextStatus]}
            </button>
          )}
          {item.status === 'rooted' && (
            <button className="prop-sheet-action prop-sheet-action--primary" onClick={() => setMode('graduate')} disabled={saving}>
              Graduate to plant
            </button>
          )}
          <button className="prop-sheet-action" onClick={() => setMode('edit')} disabled={saving}>
            Edit details
          </button>
          <button className="prop-sheet-action prop-sheet-action--danger" onClick={() => setMode('fail')} disabled={saving}>
            Mark as failed
          </button>
          <button className="prop-sheet-action prop-sheet-action--muted" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function NewBatchForm({ propagationDetailId, enclosureOptions, lightSources, onCreated, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [count, setCount] = useState('3');
  const [labelPrefix, setLabelPrefix] = useState('Item');
  const [startingNumber, setStartingNumber] = useState('1');
  const [sourceLabel, setSourceLabel] = useState('');
  const [startedDate, setStartedDate] = useState(new Date().toISOString().split('T')[0]);
  const [callusedAt, setCallusedAt] = useState('');
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [setupValues, setSetupValues] = useState({
    prop_substrate: '',
    prop_enclosure: '',
    prop_light_source_id: '',
    prop_heat_mat: 0,
    prop_heat_target_f: '',
  });

  useEffect(() => {
    fetch('/locations')
      .then(r => r.ok ? r.json() : [])
      .then(data => setLocations(data))
      .catch(() => {});
  }, []);

  function handleSetupChange(key, value) {
    setSetupValues(prev => ({ ...prev, [key]: value }));
  }

  const previewLabel = `${labelPrefix || 'Item'} ${parseInt(startingNumber, 10) || 1}`;

  async function handleSubmit(e) {
    e.preventDefault();
    const n = parseInt(count, 10);
    if (!n || n < 1 || n > 50) { setError('Count must be 1–50'); return; }
    const startNum = parseInt(startingNumber, 10) || 1;
    const prefix = labelPrefix.trim() || 'Item';
    setSaving(true);
    setError(null);
    try {
      const itemDefaults = {
        prop_substrate:       setupValues.prop_substrate || null,
        prop_enclosure:       setupValues.prop_enclosure || null,
        prop_light_source_id: setupValues.prop_light_source_id ? parseInt(setupValues.prop_light_source_id, 10) : null,
        prop_heat_mat:        setupValues.prop_heat_mat ? 1 : 0,
        prop_heat_target_f:   setupValues.prop_heat_mat && setupValues.prop_heat_target_f ? parseInt(setupValues.prop_heat_target_f, 10) : null,
      };
      const items = Array.from({ length: n }, (_, i) => ({
        label: `${prefix} ${startNum + i}`,
        ...itemDefaults,
      }));
      const res = await fetch('/propagation-items/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propagation_detail_id: propagationDetailId,
          started_date: startedDate || null,
          callused_at: callusedAt || null,
          location_id: locationId ? parseInt(locationId, 10) : null,
          source_label: sourceLabel.trim() || null,
          items,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      onCreated(data.items);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="prop-batch-form">
      <h3 className="prop-batch-form-title">Create batch</h3>
      {error && <p className="health-save-error">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">Number of items</label>
            <input type="number" value={count} onChange={e => setCount(e.target.value)} className="form-input" min="1" max="50" />
          </div>
          <div className="form-field">
            <label className="form-label">Label prefix</label>
            <input type="text" value={labelPrefix} onChange={e => setLabelPrefix(e.target.value)} className="form-input" placeholder="Item" />
          </div>
          <div className="form-field">
            <label className="form-label">Starting number</label>
            <input type="number" value={startingNumber} onChange={e => setStartingNumber(e.target.value)} className="form-input" min="1" placeholder="1" />
          </div>
          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label" style={{ color: 'var(--text-light)', fontSize: '0.8rem' }}>
              Labels will be: <strong>{previewLabel}</strong>, {labelPrefix || 'Item'} {(parseInt(startingNumber, 10) || 1) + 1}, …
            </label>
          </div>
          <div className="form-field">
            <label className="form-label">Started date</label>
            <input type="date" value={startedDate} onChange={e => setStartedDate(e.target.value)} className="form-input" />
          </div>
          <div className="form-field">
            <label className="form-label">Callused date (optional)</label>
            <input type="date" value={callusedAt} onChange={e => setCallusedAt(e.target.value)} className="form-input" />
          </div>
          <div className="form-field">
            <label className="form-label">Location (optional)</label>
            <select value={locationId} onChange={e => setLocationId(e.target.value)} className="form-input">
              <option value="">— None —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">Source label (optional)</label>
            <input type="text" value={sourceLabel} onChange={e => setSourceLabel(e.target.value)} className="form-input" placeholder="e.g. Flask A, Batch Spring 2026" />
          </div>
        </div>

        <p className="prop-batch-section-label">Propagation setup (applies to all items)</p>
        <PropSetupFields
          values={setupValues}
          onChange={handleSetupChange}
          enclosureOptions={enclosureOptions}
          lightSources={lightSources}
          showNodePosition={false}
        />

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
            {saving ? 'Creating…' : 'Create items'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function PropagationBay() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fieldOptions = useFieldOptions();
  const lossReasonOptions = fieldOptionList(fieldOptions, 'loss_reason');
  const enclosureOptions  = fieldOptionList(fieldOptions, 'prop_enclosure');

  const [lightSources, setLightSources] = useState([]);
  useEffect(() => {
    fetch('/light-sources')
      .then(r => r.ok ? r.json() : [])
      .then(data => setLightSources(data))
      .catch(() => {});
  }, []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const newPropDetailId      = searchParams.get('new')    ? parseInt(searchParams.get('new'),    10) : null;
  const focusedPropDetailId  = searchParams.get('detail') ? parseInt(searchParams.get('detail'), 10) : null;
  const [showNewBatch, setShowNewBatch] = useState(Boolean(newPropDetailId));
  const groupRefs = useRef({});

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/propagation-items');
      if (!res.ok) throw new Error('Failed to load propagation items');
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Scroll to and flash-highlight the group linked from a Plant Detail "Manage batch" link
  useEffect(() => {
    if (!focusedPropDetailId || loading) return;
    const el = groupRefs.current[focusedPropDetailId];
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('prop-group--highlight');
      setTimeout(() => el.classList.remove('prop-group--highlight'), 1800);
    }, 100);
    return () => clearTimeout(t);
  }, [focusedPropDetailId, loading]);

  // Group items by propagation_detail_id so each observation/batch stays separate.
  // Items from the same parent plant but different propagation events get distinct cards.
  const groups = items.reduce((acc, item) => {
    const key = item.propagation_detail_id != null
      ? `pd-${item.propagation_detail_id}`
      : `ext-${item.parent_plant_id ?? 'unknown'}`;
    if (!acc[key]) acc[key] = {
      parentPlantId: item.parent_plant_id,
      propagationDetailId: item.propagation_detail_id,
      startedDate: item.started_date || item.created_at || '',
      items: [],
    };
    acc[key].items.push(item);
    return acc;
  }, {});

  const sortedGroups = Object.values(groups).sort((a, b) => {
    // Linked batches (tied to an observation) before standalone
    if (a.parentPlantId && !b.parentPlantId) return -1;
    if (!a.parentPlantId && b.parentPlantId) return 1;
    // Most recently started batch first
    return (b.startedDate || '').localeCompare(a.startedDate || '');
  });

  function exitSelectionMode() {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setConfirmDelete(false);
    setBulkError(null);
  }

  function toggleSelect(id) {
    if (selectedIds.has(id) && selectedIds.size === 1) {
      exitSelectionMode();
      return;
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setConfirmDelete(false);
  }

  function selectAll() { setSelectedIds(new Set(items.map(i => i.id))); setConfirmDelete(false); }

  async function handleBulkCheckIn() {
    setBulkSaving(true); setBulkError(null);
    const today = new Date().toISOString().split('T')[0];
    try {
      await Promise.all([...selectedIds].map(id =>
        fetch(`/propagation-items/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ last_checked_at: today }),
        }).then(r => { if (!r.ok) throw new Error('Failed'); })
      ));
      setItems(prev => prev.map(i => selectedIds.has(i.id) ? { ...i, last_checked_at: today } : i));
      exitSelectionMode();
    } catch {
      setBulkError('Some items failed to update — try again.');
    } finally {
      setBulkSaving(false);
    }
  }

  async function handleBulkDelete() {
    setBulkSaving(true); setBulkError(null);
    try {
      await Promise.all([...selectedIds].map(id =>
        fetch(`/propagation-items/${id}`, { method: 'DELETE' })
          .then(r => { if (!r.ok) throw new Error('Failed'); })
      ));
      setItems(prev => prev.filter(i => !selectedIds.has(i.id)));
      exitSelectionMode();
    } catch {
      setBulkError('Some items failed to delete — try again.');
    } finally {
      setBulkSaving(false);
    }
  }

  function handleItemUpdated(updatedItem) {
    if (updatedItem === null) {
      loadItems();
    } else {
      setItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i)
        .filter(i => i.status !== 'failed' && !i.child_plant_id));
    }
  }

  if (error) {
    return (
      <div className="app">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => navigate('/', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Propagation Bay</h1>
          </div>
        </header>
        <ErrorScreen message={error} onRetry={loadItems} />
      </div>
    );
  }

  return (
    <div className="app">
      <header className="sub-header">
        <button
          className="sub-header-back"
          onClick={selectionMode ? exitSelectionMode : () => navigate('/', { replace: true })}
          aria-label={selectionMode ? 'Exit selection' : 'Back'}
          title={selectionMode ? 'Exit selection' : 'Back'}
        >
          <Icon name={selectionMode ? 'x' : 'arrowLeft'} size={20} />
        </button>
        <div className="sub-header-title">
          <h1>{selectionMode ? `${selectedIds.size} selected` : 'Propagation Bay'}</h1>
          {!selectionMode && items.length > 0 && (
            <p className="sub-header-subtitle">{items.length} active</p>
          )}
        </div>
        <div className="sub-header-actions">
          {selectionMode ? (
            <button
              className="btn-header-ghost"
              onClick={selectedIds.size === items.length
                ? () => { setSelectedIds(new Set()); setConfirmDelete(false); }
                : selectAll}
            >
              {selectedIds.size === items.length ? 'Deselect all' : 'Select all'}
            </button>
          ) : items.length > 0 && (
            <button
              className="btn-header-ghost"
              onClick={() => { setSelectionMode(true); setActiveItem(null); }}
            >
              Select
            </button>
          )}
        </div>
      </header>

      <div className="page-content" style={{ padding: '1rem', paddingBottom: selectionMode && selectedIds.size > 0 ? '5.5rem' : '1rem' }}>
        {newPropDetailId && showNewBatch && !selectionMode && (
          <NewBatchForm
            propagationDetailId={newPropDetailId}
            enclosureOptions={enclosureOptions}
            lightSources={lightSources}
            onCreated={() => {
              setSearchParams({});
              setShowNewBatch(false);
              loadItems();
            }}
            onCancel={() => {
              setSearchParams({});
              setShowNewBatch(false);
            }}
          />
        )}

        {loading ? (
          <p className="health-loading">Loading…</p>
        ) : items.length === 0 && !showNewBatch ? (
          <div className="prop-bay-empty">
            <p className="prop-bay-empty-title">No active propagation items</p>
            <p className="prop-bay-empty-sub">Start a batch from a propagation observation on any plant.</p>
          </div>
        ) : (
          sortedGroups.map(group => (
            <GroupSection
              key={`${group.parentPlantId ?? 'ext'}-${group.propagationDetailId ?? 'unknown'}`}
              group={group}
              groupRef={group.propagationDetailId ? (el => { groupRefs.current[group.propagationDetailId] = el; }) : null}
              onAction={setActiveItem}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onSelect={(id, enterMode) => {
                if (enterMode) { setSelectionMode(true); setActiveItem(null); setSelectedIds(new Set([id])); }
                else toggleSelect(id);
              }}
            />
          ))
        )}
      </div>

      {selectionMode && selectedIds.size > 0 && createPortal(
        <div className="prop-bulk-bar">
          <div className="prop-bulk-handle" />
          {bulkError && <p className="prop-bulk-error">{bulkError}</p>}
          {confirmDelete ? (
            <>
              <button disabled={bulkSaving} onClick={handleBulkDelete} className="prop-bulk-btn prop-bulk-btn--delete-confirm">
                {bulkSaving ? 'Deleting…' : `Delete ${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
              <button disabled={bulkSaving} onClick={() => setConfirmDelete(false)} className="prop-bulk-btn prop-bulk-btn--cancel">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button disabled={bulkSaving} onClick={handleBulkCheckIn} className="prop-bulk-btn prop-bulk-btn--checkin">
                {bulkSaving ? 'Saving…' : `Check in ${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
              <button disabled={bulkSaving} onClick={() => setConfirmDelete(true)} className="prop-bulk-btn prop-bulk-btn--delete">
                Delete {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''}…
              </button>
            </>
          )}
        </div>,
        document.body
      )}

      <BottomSheet
        open={Boolean(activeItem) && !selectionMode}
        onClose={() => setActiveItem(null)}
        title={activeItem?.label || (activeItem ? `Item ${activeItem.id}` : '')}
        subtitle={activeItem ? STATUS_LABELS[activeItem.status] : undefined}
        maxHeight="82dvh"
      >
        {activeItem && (
          <ItemActionSheet
            item={activeItem}
            lossReasonOptions={lossReasonOptions}
            enclosureOptions={enclosureOptions}
            lightSources={lightSources}
            onClose={() => setActiveItem(null)}
            onUpdated={updated => {
              handleItemUpdated(updated);
              setActiveItem(null);
            }}
          />
        )}
      </BottomSheet>
    </div>
  );
}

const PROPAGATION_METHOD_LABELS = {
  cutting: 'Cutting', wet_stick: 'Wet Stick', division: 'Division',
  offset_pup: 'Offset / Pup', corm: 'Corm', air_layer: 'Air Layer',
  tissue_culture: 'TC', runner_stolon: 'Stolon', rhizome_division: 'Rhizome Div.',
  bulbil: 'Bulbil', graft: 'Graft', other_clone: 'Clone',
};

function GroupSection({ group, groupRef, onAction, selectionMode, selectedIds, onSelect }) {
  const [collapsed, setCollapsed] = useState(false);
  const [parentPlant, setParentPlant] = useState(null);

  useEffect(() => {
    if (!group.parentPlantId) return;
    fetch(`/plants/${group.parentPlantId}`)
      .then(r => r.ok ? r.json() : null)
      .then(p => setParentPlant(p))
      .catch(() => {});
  }, [group.parentPlantId]);

  const displayName = parentPlant
    ? plantDisplayName(parentPlant)
    : group.parentPlantId
      ? `Plant #${group.parentPlantId}`
      : 'External source';

  const statusCounts = group.items.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {});

  // Batch-level metadata from first item (all items in group share these)
  const rep = group.items[0];
  const batchMethod     = rep?.propagation_method;
  const batchDate       = rep?.started_date;
  const batchSourceLabel = rep?.source_label;

  const heroPhotoId = parentPlant?.cover_photo_id ?? null;
  const heroOffsetX = parentPlant?.cover_photo_offset_x ?? 50;
  const heroOffsetY = parentPlant?.cover_photo_offset_y ?? 50;

  return (
    <div ref={groupRef} className="prop-group">
      {heroPhotoId && group.parentPlantId ? (
        <div style={{ position: 'relative' }}>
          <Link to={`/plants/${group.parentPlantId}`} state={{ returnTo: '/propagation' }} style={{ display: 'block', textDecoration: 'none' }}>
            <div style={{ position: 'relative', height: 140, overflow: 'hidden', background: 'var(--surface-recessed, #f0f0f0)' }}>
              <img
                src={photoUrl(heroPhotoId, parentPlant?.cover_photo_rotation_version, parentPlant?.cover_photo_created_at)}
                alt={displayName}
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  objectPosition: `${heroOffsetX}% ${heroOffsetY}%`,
                  display: 'block',
                }}
              />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)',
                padding: '1.5rem 0.75rem 0.6rem',
              }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#fff', lineHeight: 1.2, display: 'block' }}>{displayName}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.18rem' }}>
                  {parentPlant?.specimen_id && (
                    <span style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>({parentPlant.specimen_id})</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: '0.73rem', color: '#fff', fontWeight: 600, background: 'rgba(0,0,0,0.35)', borderRadius: 6, padding: '0.08rem 0.45rem', whiteSpace: 'nowrap' }}>
                    {Object.entries(statusCounts).map(([s, c]) => `${c} ${STATUS_LABELS[s] || s}`).join(' · ')}
                  </span>
                </div>
                {(batchMethod || batchDate || batchSourceLabel) && (
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.2rem' }}>
                    {batchMethod && <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)' }}>{PROPAGATION_METHOD_LABELS[batchMethod] || batchMethod}</span>}
                    {batchDate && <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>started {batchDate}</span>}
                    {batchSourceLabel && <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>{batchSourceLabel}</span>}
                  </div>
                )}
              </div>
            </div>
          </Link>
          <button
            type="button"
            onClick={() => setCollapsed(c => !c)}
            aria-expanded={!collapsed}
            style={{
              position: 'absolute', top: '0.5rem', right: '0.5rem',
              background: 'rgba(0,0,0,0.35)', border: 'none', borderRadius: 6,
              color: '#fff', cursor: 'pointer', padding: '0.2rem 0.4rem',
              fontSize: '0.75rem', lineHeight: 1, display: 'flex', alignItems: 'center', gap: '0.25rem',
            }}
          >
            <Icon name={collapsed ? 'chevronDown' : 'chevronUp'} size={13} />
          </button>
        </div>
      ) : (
        <div className="prop-group-header">
          <div className="prop-group-name-row">
            {group.parentPlantId ? (
              <Link to={`/plants/${group.parentPlantId}`} state={{ returnTo: '/propagation' }} className="prop-group-name">
                {displayName}
              </Link>
            ) : (
              <span className="prop-group-name">{displayName}</span>
            )}
            {parentPlant?.specimen_id && (
              <span className="prop-group-name-id">({parentPlant.specimen_id})</span>
            )}
            <span className="prop-group-status-summary">
              {Object.entries(statusCounts).map(([s, c]) => `${c} ${STATUS_LABELS[s] || s}`).join(' · ')}
            </span>
            <button
              type="button"
              onClick={() => setCollapsed(c => !c)}
              aria-expanded={!collapsed}
              className="prop-group-collapse-btn"
            >
              <Icon name={collapsed ? 'chevronDown' : 'chevronUp'} size={15} />
            </button>
          </div>
          {(batchMethod || batchDate || batchSourceLabel) && (
            <div className="prop-group-meta-row">
              {batchMethod && <span className="prop-group-meta-span">{PROPAGATION_METHOD_LABELS[batchMethod] || batchMethod}</span>}
              {batchDate && <span className="prop-group-meta-span">started {batchDate}</span>}
              {batchSourceLabel && <span className="prop-group-meta-span">{batchSourceLabel}</span>}
            </div>
          )}
        </div>
      )}
      {!collapsed && (
        <div className="prop-group-items">
          {group.items.map(item => (
            <ItemCard
              key={item.id} item={item} onAction={onAction}
              selectionMode={selectionMode}
              selected={selectedIds?.has(item.id) || false}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
